import {useState, type CSSProperties, type ReactNode, type ReactElement} from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {Button} from '@/components/app/button';
import {Icon, type IconProp} from '@/app/components/Icon';
import {cn} from '@/lib/utils';

export interface MenuAction {
  label?: string;
  icon?: IconProp;
  onSelect?: () => unknown;
  visible?: boolean;
  disabled?: boolean;
  separator?: boolean;
  url?: string;
  target?: string;
  template?: (item: MenuAction) => ReactNode;
  className?: string;
}
export function ActionMenu({
  actions,
  children,
  className,
  style,
  align = 'end',
  onShow,
  onHide,
  enabled = true,
}: {
  actions?: MenuAction[];
  children: ReactElement;
  className?: string;
  style?: CSSProperties;
  align?: 'start' | 'center' | 'end';
  onShow?: () => void;
  onHide?: () => void;
  enabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu
      open={open && enabled}
      onOpenChange={(next) => {
        setOpen(next && enabled);
        if (next && enabled) onShow?.();
        else onHide?.();
      }}
    >
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className={className} style={style}>
        {actions
          ?.filter((action) => action.visible !== false)
          .map((action, index) =>
            action.separator ? (
              <DropdownMenuSeparator key={index} />
            ) : (
              <DropdownMenuItem
                key={index}
                disabled={action.disabled}
                // Larger touch targets on phones, like PrimeReact's menu items.
                className={cn('py-2.5 text-base sm:py-1.5 sm:text-sm', action.className)}
                onSelect={() => {
                  if (action.url) window.open(action.url, action.target ?? '_self');
                  else void action.onSelect?.();
                }}
              >
                {action.icon && <Icon icon={action.icon} />} {action.label}
              </DropdownMenuItem>
            ),
          )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
export type RouteTabItem = Omit<MenuAction, 'template'> & {
  template?: (item: RouteTabItem, options: RouteTabOptions) => ReactNode;
};
export interface RouteTabOptions {
  active: boolean;
  className: string;
  onSelect: () => void;
}
// Folder-style tabs that sit on top of the panel below them. The bar is a size
// container: labels, badges and icons collapse as it narrows (see `routeTabParts`).
const routeTabClass =
  'group inline-flex min-w-0 max-w-46 flex-1 items-center justify-center gap-2 rounded-t-xl px-3 py-3 text-sm font-semibold no-underline outline-none transition-colors hover:no-underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset data-[active=false]:bg-zinc-200 data-[active=false]:text-muted-foreground data-[active=false]:hover:bg-zinc-100 data-[active=true]:bg-card data-[active=true]:text-foreground';
export const routeTabParts = {
  icon: 'shrink-0 @min-[650px]:@max-[768px]:hidden',
  label: 'truncate @max-[650px]:hidden',
  badge:
    'shrink-0 @max-[500px]:hidden bg-zinc-400/60 text-foreground group-data-[active=true]:bg-navigation group-data-[active=true]:text-navigation-foreground',
};
export function RouteTabs({
  items = [],
  activeIndex = 0,
  onActiveChange,
  className,
  'aria-label': ariaLabel = 'Views',
}: {
  items?: RouteTabItem[];
  activeIndex?: number;
  onActiveChange?: (index: number) => void;
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <nav aria-label={ariaLabel} className={cn('@container', className)}>
      <ul className="m-0 flex list-none items-end gap-1 p-0">
        {items
          .filter((item) => item.visible !== false)
          .map((item, index) => {
            const active = index === activeIndex;
            const options = {active, className: routeTabClass, onSelect: () => onActiveChange?.(index)};
            return (
              <li key={index} className="flex min-w-0 max-w-46 flex-1">
                {item.template ? (
                  item.template(item, options)
                ) : (
                  <button
                    type="button"
                    data-active={active}
                    aria-current={active ? 'page' : undefined}
                    disabled={item.disabled}
                    className={routeTabClass}
                    onClick={options.onSelect}
                  >
                    {item.icon && <Icon icon={item.icon} className={routeTabParts.icon} />}
                    <span className={item.icon ? routeTabParts.label : 'truncate'}>{item.label}</span>
                  </button>
                )}
              </li>
            );
          })}
      </ul>
    </nav>
  );
}
export function Breadcrumbs({
  items = [],
  home,
  className,
}: {
  items?: MenuAction[];
  home?: MenuAction;
  className?: string;
}) {
  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="m-0 flex list-none items-center gap-2 p-0 text-sm">
        {(home ? [home, ...items] : items).map((item, index) => (
          <li key={index} className="flex items-center gap-2">
            {index > 0 && <span aria-hidden>/</span>}
            {item.template ? (
              item.template(item)
            ) : item.url ? (
              <a
                className={cn('inline-flex items-center gap-2', item.className)}
                aria-label={item === home && !item.label ? 'Home' : undefined}
                href={item.url}
                onClick={() => void item.onSelect?.()}
              >
                {item.icon && <Icon icon={item.icon} />} {item.label}
              </a>
            ) : item.onSelect ? (
              <button
                type="button"
                className={cn('inline-flex items-center gap-2 cursor-pointer', item.className)}
                aria-label={item === home && !item.label ? 'Home' : undefined}
                disabled={item.disabled}
                onClick={() => void item.onSelect?.()}
              >
                {item.icon && <Icon icon={item.icon} />} {item.label}
              </button>
            ) : (
              <span className={cn('inline-flex items-center gap-2', item.className)}>
                {item.icon && <Icon icon={item.icon} />} {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
export function StepNavigation({
  items = [],
  activeIndex = 0,
  onActiveChange,
  readOnly = true,
  className,
  completeColor,
}: {
  items?: MenuAction[];
  activeIndex?: number;
  onActiveChange?: (index: number) => void;
  readOnly?: boolean;
  className?: string;
  /** Color for steps before the active one; defaults to the primary color. */
  completeColor?: string;
}) {
  const style = {'--step-complete': completeColor || 'var(--primary)'} as CSSProperties;
  // Fixed-width items (14rem) so decorations drawn around the steps can be positioned against them.
  return (
    <nav aria-label="Subscription steps" className={className} style={style}>
      <ol className="m-0 flex list-none p-0">
        {items.map((item, index) => {
          const state = index === activeIndex ? 'active' : index < activeIndex ? 'complete' : 'upcoming';
          return (
            <li
              key={index}
              className="flex h-24 w-56 shrink-0 items-center"
              aria-current={state === 'active' ? 'step' : undefined}
            >
              <button
                type="button"
                data-state={state}
                disabled={readOnly || item.disabled}
                onClick={() => onActiveChange?.(index)}
                className="group flex items-center gap-2 rounded-md p-1 text-sm font-medium text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default data-[state=active]:text-foreground data-[state=complete]:text-(--step-complete)"
              >
                <span className="grid size-8 shrink-0 place-items-center rounded-full bg-zinc-400 text-white group-data-[state=active]:bg-primary group-data-[state=active]:ring-2 group-data-[state=active]:ring-primary group-data-[state=active]:ring-offset-2 group-data-[state=complete]:bg-(--step-complete)">
                  {index + 1}
                </span>
                <span className="whitespace-nowrap">{item.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
export function ActivityTimeline<T>({
  items = [],
  renderContent,
  renderDate,
}: {
  items?: T[];
  renderContent: (item: T) => ReactNode;
  renderDate?: (item: T) => ReactNode;
}) {
  return (
    <ol className="m-0 list-none p-0">
      {items.map((item, index) => (
        <li className="relative grid grid-cols-[minmax(6rem,10rem)_1fr] gap-4 pb-4" key={index}>
          <div className="relative text-right text-sm text-muted-foreground">{renderDate?.(item)}</div>
          <div className="border-l-2 pl-4 before:absolute before:-ml-[1.375rem] before:size-2 before:rounded-full before:bg-primary">
            {renderContent(item)}
          </div>
        </li>
      ))}
    </ol>
  );
}
export function AvatarActions({
  actions,
  className,
  icon,
}: {
  actions?: MenuAction[];
  className?: string;
  icon?: IconProp;
}) {
  return (
    <div className={className}>
      <ActionMenu actions={actions}>
        <Button type="button" size="icon-sm" aria-label="Edit profile image">
          <Icon icon={icon ?? 'bars'} />
        </Button>
      </ActionMenu>
    </div>
  );
}
